<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-sent-template-tracking.RENDERING.1 — renders HEADER/BODY/FOOTER/BUTTONS
// whatsapp-sent-template-tracking.RENDERING.2 — wraps content in BaseBubble so
// the bubble inherits the standard MESSAGE_VARIANTS.TEMPLATE styling
// (bg-n-green-4 text-n-slate-12), MessageMeta, orientation, and in_reply_to
// preview — visually consistent with every other outbound message in Chatwoot.
//
// Renders a WhatsApp campaign template that was sent before this conversation
// existed. Materialized as message_type=template by
// KaptionSentTemplateMatcherService when the contact replies, so the original
// template appears inline above the reply in chronological order.
import { computed } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import { useMessageContext } from '../../provider.js';

const { contentAttributes } = useMessageContext();

// content_attributes arrives from Rails. Accept either snake_case or
// camelCase keys defensively, since the JSONB inside template_params is not
// always deep-converted by the dashboard serializer.
const pick = (...keys) =>
  computed(() => {
    const attrs = contentAttributes.value || {};
    for (let i = 0; i < keys.length; i += 1) {
      const value = attrs[keys[i]];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  });

const templateName = pick('templateName', 'template_name');
const templateLanguage = pick('templateLanguage', 'template_language');
const campaignId = pick('campaignId', 'campaign_id');

const templateParams = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.templateParams || attrs.template_params || {};
});

const processedParams = computed(() => {
  const tp = templateParams.value;
  return tp.processedParams || tp.processed_params || {};
});

// Rendered text (interpolated by KaptionSentTemplateRendererService at send
// time). When present, prefer this over showing raw variable values.
const rendered = computed(() => {
  const tp = templateParams.value;
  return tp.rendered || tp.Rendered || null;
});

const renderedHeader = computed(() => rendered.value?.header || '');
const renderedBody = computed(() => rendered.value?.body || '');
const renderedFooter = computed(() => rendered.value?.footer || '');
const renderedButtons = computed(() => rendered.value?.buttons || []);

const hasRendered = computed(
  () =>
    !!(
      renderedHeader.value ||
      renderedBody.value ||
      renderedFooter.value ||
      renderedButtons.value.length
    )
);

const header = computed(() => processedParams.value.header || {});

const headerMediaUrl = computed(
  () => header.value.media_url || header.value.mediaUrl
);
const headerMediaType = computed(() =>
  (header.value.media_type || header.value.mediaType || '').toLowerCase()
);

const headerKind = computed(() => {
  if (!headerMediaUrl.value) return null;
  if (headerMediaType.value.startsWith('image')) return 'image';
  if (headerMediaType.value.startsWith('video')) return 'video';
  return 'document';
});

// Fallback: when no rendered text is available (templates not synced from
// Meta, or older row before this feature was added), list the variables.
const headerTextValues = computed(() => {
  const reserved = new Set([
    'media_url',
    'media_type',
    'media_name',
    'mediaUrl',
    'mediaType',
    'mediaName',
  ]);
  return Object.entries(header.value).filter(([k, v]) => !reserved.has(k) && v);
});

const bodyEntries = computed(() =>
  Object.entries(processedParams.value.body || {}).filter(([, v]) => v)
);

const footerEntries = computed(() =>
  Object.entries(processedParams.value.footer || {}).filter(([, v]) => v)
);

const fallbackButtons = computed(() =>
  (processedParams.value.buttons || []).filter(b => b)
);

const buttonsForRender = computed(() =>
  renderedButtons.value.length ? renderedButtons.value : fallbackButtons.value
);

const buttonIcon = btn => {
  const type = (btn?.type || '').toLowerCase();
  if (type === 'url') return 'i-lucide-external-link';
  if (type === 'phone_number' || type === 'phone') return 'i-lucide-phone';
  // NOTICE: KAPTION-INTEGRATION - whatsapp-flows: a button that opens a flow
  if (type === 'flow') return 'i-lucide-list-checks';
  if (type === 'copy_code') return 'i-lucide-copy';
  return 'i-lucide-message-circle';
};

const buttonLabel = btn =>
  btn?.text || btn?.title || btn?.label || btn?.parameter || '—';
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="whatsapp-template">
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2 text-[11px]">
        <span class="i-lucide-megaphone text-kpt-amber size-3.5" />
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="text-kpt-amber font-semibold uppercase tracking-wide">
          WhatsApp template
        </span>
        <span v-if="templateLanguage" class="text-n-slate-11">
          · {{ templateLanguage }}
        </span>
        <span v-if="campaignId" class="text-n-slate-10">
          · #{{ campaignId }}
        </span>
      </div>

      <!-- Media header (image/video/document) — independent of rendered text. -->
      <div
        v-if="headerKind === 'image'"
        class="rounded-lg overflow-hidden bg-n-alpha-2"
      >
        <img
          :src="headerMediaUrl"
          :alt="templateName || 'template header'"
          class="w-full h-auto"
        />
      </div>
      <div
        v-else-if="headerKind === 'video'"
        class="rounded-lg overflow-hidden bg-n-alpha-2"
      >
        <video :src="headerMediaUrl" controls class="w-full h-auto" />
      </div>
      <a
        v-else-if="headerKind === 'document'"
        :href="headerMediaUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-2 rounded-lg bg-n-alpha-2 p-2 text-n-slate-12 text-xs hover:bg-n-alpha-3"
      >
        <span class="i-lucide-file-text size-4" />
        <span class="truncate">{{ headerMediaUrl }}</span>
      </a>

      <!-- Preferred: fully-interpolated header/body/footer (the exact message
           the recipient saw on WhatsApp). -->
      <template v-if="hasRendered">
        <div
          v-if="renderedHeader"
          class="text-sm font-semibold text-n-slate-12 whitespace-pre-wrap break-words"
        >
          {{ renderedHeader }}
        </div>
        <div
          v-if="renderedBody"
          class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
        >
          {{ renderedBody }}
        </div>
        <div
          v-if="renderedFooter"
          class="text-xs text-n-slate-11 whitespace-pre-wrap break-words"
        >
          {{ renderedFooter }}
        </div>
      </template>

      <!-- Fallback when template definition isn't available (older rows, sync
           gap): show the template name + raw parameter values. -->
      <template v-else>
        <div v-if="templateName" class="text-sm font-medium text-n-slate-12">
          {{ templateName }}
        </div>

        <div
          v-if="headerTextValues.length"
          class="flex flex-col gap-0.5 text-xs"
        >
          <div v-for="[k, v] in headerTextValues" :key="`h-${k}`">
            <span class="text-n-slate-11">{{ k }}:</span>
            <span class="text-n-slate-12 ml-1">{{ v }}</span>
          </div>
        </div>

        <div v-if="bodyEntries.length" class="flex flex-col gap-1 text-sm">
          <div v-for="[k, v] in bodyEntries" :key="`b-${k}`">
            <span class="text-n-slate-11 text-xs uppercase tracking-wide">
              {{ k }}
            </span>
            <div class="text-n-slate-12 whitespace-pre-wrap break-words">
              {{ v }}
            </div>
          </div>
        </div>

        <div
          v-if="footerEntries.length"
          class="text-xs text-n-slate-11 border-t border-n-alpha-2 pt-2"
        >
          <div v-for="[k, v] in footerEntries" :key="`f-${k}`">{{ v }}</div>
        </div>
      </template>

      <div
        v-if="buttonsForRender.length"
        class="flex flex-col gap-2 border-t border-n-alpha-2 pt-2"
      >
        <div
          v-for="(btn, i) in buttonsForRender"
          :key="`btn-${i}`"
          class="w-full text-xs font-medium text-kpt-copilot bg-kpt-copilot/10 border border-kpt-copilot/30 rounded-lg py-2 px-3 flex items-center justify-center gap-1 cursor-default select-none"
        >
          <span class="size-3.5" :class="[buttonIcon(btn)]" />
          <span>{{ buttonLabel(btn) }}</span>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
